import React, { useState, useEffect } from "react";
import { StringDtoY } from "../../../config/formattedDate";
import styles from "./calendar.module.css";

const weekDays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const Calendar = ({
  selectedDate,
  onChange,
  minDate = new Date(),
  label = "Appointment Date:",
}) => {
  const today = new Date();

  const [currentDate, setCurrentDate] = useState(
    selectedDate ? new Date(selectedDate) : today
  );

  /* ---------- FULL MONTH DAYS ---------- */
  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Start from Sunday
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    // End on Saturday
    const endDate = new Date(lastDayOfMonth);
    endDate.setDate(lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay()));

    const days = [];
    let current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const monthDays = getMonthDays(currentDate);

  /* ---------- HELPERS ---------- */

  const isDateDisabled = (date) => {
    const dateStr = StringDtoY(date);
    const minDateStr = StringDtoY(minDate);
    return date < minDate && dateStr !== minDateStr;
  };

  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    const selected = new Date(selectedDate);
    return (
      date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear()
    );
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    onChange(date);
  };

  /* ---------- MONTH / YEAR ---------- */

  const getMonthOptions = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: new Date(2000, i, 1).toLocaleString("default", {
        month: "long",
      }),
    }));
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 4 }, (_, i) => currentYear - 1 + i);
  };

  const handleMonthChange = (e) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(Number(e.target.value));
    setCurrentDate(newDate);
  };

  const handleYearChange = (e) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(Number(e.target.value));
    setCurrentDate(newDate);
  };

  useEffect(() => {
    if (selectedDate) {
      setCurrentDate(new Date(selectedDate));
    }
  }, [selectedDate]);

  /* ---------- RENDER ---------- */

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.calendarLabel}>{label}</div>

      <div className={styles.calendarHeader}>
        <div className={styles.monthYearSelectors}>
          <select
            className={styles.monthSelector}
            value={currentDate.getMonth()}
            onChange={handleMonthChange}
          >
            {getMonthOptions().map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <select
            className={styles.yearSelector}
            value={currentDate.getFullYear()}
            onChange={handleYearChange}
          >
            {getYearOptions().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.workingDaysSection}>
        {/* WEEK HEADER */}
        <div className={styles.weekHeader}>
          {weekDays.map((day) => (
            <div key={day} className={styles.weekHeaderDay}>
              {day}
            </div>
          ))}
        </div>

        {/* FULL MONTH GRID */}
        <div className={styles.weekDaysContainer}>
          {monthDays.map((date, index) => {
            const isDisabled = isDateDisabled(date);
            const isSelected = isDateSelected(date);
            const isCurrentMonth =
              date.getMonth() === currentDate.getMonth();

            return (
              <div key={index} className={styles.dayContainer}>
                <button
                  type="button"
                  className={`${styles.dayButton}
                    ${isSelected ? styles.selectedDay : ""}
                    ${isDisabled ? styles.disabledDay : ""}
                    ${!isCurrentMonth ? styles.otherMonth : ""}
                  `}
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabled}
                >
                  <span className={styles.dayNumber}>
                    {date.getDate()}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
